import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import PropTypes from 'prop-types';

const FilterPopoverContext = createContext(null);

export const useFilterPopover = () => useContext(FilterPopoverContext);

export const FilterPopoverProvider = ({ children, dataSignature }) => {
	const [filterTarget, setFilterTarget] = useState(null);
	const [isApplying, setIsApplying] = useState(false);
	const isApplyingRef = useRef(false);
	const dataAtApplyRef = useRef(dataSignature);

	const closeFilter = useCallback(() => {
		setFilterTarget(null);
		setIsApplying(false);
		isApplyingRef.current = false;
	}, []);

	const openFilter = useCallback((columnData) => {
		if (isApplyingRef.current) return;
		setFilterTarget(columnData);
	}, []);

	const startApplying = useCallback(() => {
		isApplyingRef.current = true;
		dataAtApplyRef.current = dataSignature;
		setIsApplying(true);
	}, [dataSignature]);

	// Close only when loader data actually changes
	useEffect(() => {
		if (!isApplying) return;

		if (dataSignature !== dataAtApplyRef.current) {
			closeFilter();
		}
	}, [dataSignature, isApplying, closeFilter]);

	const value = useMemo(
		() => ({
			filterTarget,
			isApplying,
			isApplyingRef,
			openFilter,
			closeFilter,
			startApplying,
			activeFilterId: filterTarget?.id ?? null,
		}),
		[filterTarget, isApplying, openFilter, closeFilter, startApplying],
	);

	return (
		<FilterPopoverContext.Provider value={value}>
			{children}
		</FilterPopoverContext.Provider>
	);
};

FilterPopoverProvider.propTypes = {
	children: PropTypes.node,
	dataSignature: PropTypes.any,
};
